import { all, call, fork, put, select, take } from 'redux-saga/effects';

import { getAllInstruments } from '!core/redux/userInstruments/userInstruments.selectors';
import { securityAccountSelectedSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { trlChartsServices } from '»components/molecules/TrlChart/trlCharts.services';
import { handleSagaError } from '»utils/handleSagasError';
import { joinChartsData } from '»utils/joinChartsData';

import { webAreasServices } from '../redux/webAreas/webAreas.services';
import { marketStockExchangeSlice } from './_marketStockExchange.slice';
import { marketStockExchangeServices } from './marketStockExchange.services';

const marketStockExchangeWorker = function* ({ webAreaId, getCharts }) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);
    const userInstrumentsData = yield select(getAllInstruments);

    const marketStockExchange = yield call(
      webAreasServices.webAreaInstrQuotes,
      {
        account: securityAccount,
        webPage: 'QUOTES_MARKET',
        webArea: webAreaId,
      },
    );

    const respElementIndex = marketStockExchange.findIndex(
      (el) => el.type === 'resp',
    );

    marketStockExchange.splice(respElementIndex, respElementIndex >= 0 ? 1 : 0);

    const mData = mergeData(marketStockExchange, userInstrumentsData);

    let mDataWithCharts = [];

    if (getCharts) {
      const charts = yield call(webAreasServices.webAreaGraphQuotes, {
        account: securityAccount,
        webPage: 'QUOTES_MARKET',
        webArea: webAreaId,
      });

      mDataWithCharts = joinChartsData(mData, charts);
    }

    yield put(
      marketStockExchangeSlice.actions.marketStockExchangeSucess(
        getCharts ? mDataWithCharts : mData,
      ),
    );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled)
      yield put(marketStockExchangeSlice.actions.marketStockExchangeFailure());
  }
};

const marketStockExchangeSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const data = yield take(
          marketStockExchangeSlice.actions.marketStockExchangeRequest.type,
        );

        const { webAreaId, getCharts } = data.payload;

        yield call(marketStockExchangeWorker, { webAreaId, getCharts });
      }
    } catch {
      yield put(marketStockExchangeSlice.actions.marketStockExchangeFailure());
    }
  }
};

const mergeData = (originalData, extraFields) => {
  const newData = [];
  originalData.forEach((value) => {
    const newFields = extraFields.find(
      (field) =>
        field.securityType === value.securityType &&
        field.symbol === value.symbol &&
        field.exchange === value.exchange,
    );

    newData.push({ ...newFields, ...value });
  });
  return newData;
};

const marketStockExchangeInstrumentWorker = function* ({
  symbol,
  exchange,
  securityType,
}) {
  try {
    const marketStockExchangeInstrument = yield call(
      marketStockExchangeServices.wsbGetInstrumentQuotes,
      { symbol, exchange, securityType },
    );

    const chart = yield call(trlChartsServices.instrumentGraphQuotes, {
      symbol,
      exchange,
      securityType,
    });

    const finalData = { ...marketStockExchangeInstrument[0], chartData: chart };

    yield put(
      marketStockExchangeSlice.actions.marketStockExchangeInstrumentSucess(
        finalData,
      ),
    );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled)
      yield put(
        marketStockExchangeSlice.actions.marketStockExchangeInstrumentFailure(),
      );
  }
};

const marketStockExchangeInstrumentSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const data = yield take(
          marketStockExchangeSlice.actions.marketStockExchangeInstrumentRequest
            .type,
        );

        const { symbol, exchange, securityType } = data.payload;

        yield call(marketStockExchangeInstrumentWorker, {
          symbol,
          exchange,
          securityType,
        });
      }
    } catch {
      yield put(
        marketStockExchangeSlice.actions.marketStockExchangeInstrumentFailure(),
      );
    }
  }
};

export const stockExchangeSagas = function* () {
  try {
    yield all([
      fork(marketStockExchangeSagas),
      fork(marketStockExchangeInstrumentSagas),
    ]);
  } catch {}
};
